import { getHTMLConfig } from 'pages/Homepages/hooks/useHTMLConfig';
import { isPluginContentDashlet } from './usePluginContentBlock';
import { isCustomCodeDashlet, isHostChromeDisabled } from './customCodeBlock';

type StyleSettings = Record<string, any>;

type ChromeDashlet = {
  config?: {
    chartType?: string;
    content?: unknown;
    feExtraInfo?: {
      customStylesEnabled?: boolean;
      dashletStyleConfig?: StyleSettings;
    };
  };
};

type HTMLBlockConfig = {
  height?: number;
  maxWidth?: number;
  hasShadow?: boolean;
  backgroundColor?: string;
  columns?: number;
};

export type DashletChrome = {
  htmlBlockConfig: HTMLBlockConfig | undefined;
  hasHeaderOverlay: boolean;
  dashletStyleConfig: StyleSettings | null | undefined;
  borderRadius: string | false;
  titleBackgroundColor: string;
  backgroundColor: string | undefined;
  hasShadow: boolean | undefined;
};

const getBlockConfig = getHTMLConfig as (
  dashlet: ChromeDashlet,
  defaultStyleSettings: StyleSettings | null | undefined
) => { htmlBlockConfig?: HTMLBlockConfig };

const isNumber = (value: unknown): value is number => typeof value === 'number';

export const getDashletChrome = (
  dashlet: ChromeDashlet,
  defaultStyleSettings: StyleSettings | null | undefined
): DashletChrome => {
  const { htmlBlockConfig } = getBlockConfig(dashlet, defaultStyleSettings);
  const isPluginBlock = isPluginContentDashlet(dashlet);
  const isCustomCode = isCustomCodeDashlet(dashlet);
  const hasHeaderOverlay =
    Boolean(htmlBlockConfig) || isPluginBlock || isCustomCode;

  if (isCustomCode && isHostChromeDisabled(dashlet.config?.content)) {
    return {
      htmlBlockConfig,
      hasHeaderOverlay,
      dashletStyleConfig: {},
      borderRadius: '0px',
      titleBackgroundColor: 'transparent',
      backgroundColor: 'transparent',
      hasShadow: false,
    };
  }

  const dashletStyleDisabled =
    isCustomCode || !dashlet.config?.feExtraInfo?.customStylesEnabled;

  const dashletStyleConfig =
    htmlBlockConfig || isPluginBlock
      ? {}
      : dashletStyleDisabled
        ? defaultStyleSettings
        : {
            ...defaultStyleSettings,
            ...dashlet.config?.feExtraInfo?.dashletStyleConfig,
          };

  const borderRadius =
    isNumber(dashletStyleConfig?.topLeftBorderRadius) &&
    isNumber(dashletStyleConfig?.topRightBorderRadius) &&
    isNumber(dashletStyleConfig?.bottomLeftBorderRadius) &&
    isNumber(dashletStyleConfig?.bottomRightBorderRadius) &&
    `${dashletStyleConfig?.topLeftBorderRadius}px ${dashletStyleConfig?.topRightBorderRadius}px ${dashletStyleConfig?.bottomRightBorderRadius}px ${dashletStyleConfig?.bottomLeftBorderRadius}px`;
  const isTransparent =
    dashletStyleConfig?.titleBackgroundColor?.includes('rgba') &&
    dashletStyleConfig?.titleBackgroundColor?.split(',')[3] !== '1)';

  const titleBackgroundColor =
    isTransparent || hasHeaderOverlay
      ? 'transparent'
      : dashletStyleConfig?.titleBackgroundColor ??
        'var(--color-background-white)';

  const pluginBlockDropShadow =
    dashlet.config?.feExtraInfo?.dashletStyleConfig?.dropShadow ?? false;

  return {
    htmlBlockConfig,
    hasHeaderOverlay,
    dashletStyleConfig,
    borderRadius,
    titleBackgroundColor,
    backgroundColor: isPluginBlock
      ? 'transparent'
      : htmlBlockConfig?.backgroundColor,
    hasShadow: isPluginBlock
      ? pluginBlockDropShadow
      : htmlBlockConfig?.hasShadow,
  };
};
